// createDashboard(config) — the dashboard app's BOOTSTRAP (IA_ABSORPTION_PLAN Phase 1 steps 6b-2a
// and 6b-2b). It creates, in this order:
//
//   the dashboard  (6b-2b, ./dashboardContext.js) the platform state and services — URL params,
//            reactive settings, THE proxy fetcher + ApiClient, DuckDBService, the data loader over
//            config.data.manifest, the filter contexts of config.filters, analytics. Provided to
//            the app; components reach it with useDashboard(). Creating it starts nothing: the
//            app's root component calls `dashboard.start()` on mount (URL validation, settings,
//            analytics init, user id, token refresh, DuckDB init — in that order)
//   router  (config.routes + config.history, or a ready config.router; GA page tracking when
//            config.analytics is set; config.afterEach)
//   i18n    (vue-i18n COMPOSITION mode, fallback 'en', the app's messages merged OVER the
//            vocabulary it passes; its locale follows the dashboard's settings.locale, or the
//            config.i18n.locale watch source)
//   Vuetify (createDashboardVuetify from @rdrive/theme with config.vuetify)
//   store   config.store — a Vue plugin, or a factory `(dashboard) => plugin` (6b-2b: an app
//            store whose actions need the dashboard)
//   app     createApp(config.App) → provide(dashboard) → use(router) → use(Vuetify) →
//            use(store) → use(i18n)
//
// and returns the app, unmounted: `createDashboard(config).mount('#app')`.
//
// What it does NOT own: global CSS imports (the app's main.js — they are bundle-level and their
// order is the cascade), the store's contents (an opaque Vue plugin, never imported or read here:
// PLATFORM.md §3.2 rule 1), Vuetify's component/directive maps and icon sets (app-passed on
// purpose: dropping the wildcard maps is the app's own opt-in change, PACKAGING_PLAN.md §3.4),
// the vocabulary (packages never import @rdrive/vocabulary — the app passes it), any SQL or table
// name (the manifest, the filter configs and the onLoaded hook are the app's), and WHEN data
// loads (the app calls `dashboard.loader.load()`).
import { createApp, watch } from 'vue';
import {
  createRouter,
  createWebHistory,
  createWebHashHistory,
  createMemoryHistory,
} from 'vue-router';
import { createI18n } from 'vue-i18n';
import { createDashboardVuetify } from '@rdrive/theme';
import { DEFAULT_LOCALE, mergeMessages } from '@rdrive/i18n';
import { createDashboardContext } from './dashboardContext.js';
import { DASHBOARD_KEY } from './useDashboard.js';

const PREFIX = '@rdrive/plugin-dashboard-shell: createDashboard';

const HISTORIES = {
  web: createWebHistory,
  hash: createWebHashHistory,
  memory: createMemoryHistory,
};

const isRouter = (value) =>
  !!value && typeof value.install === 'function' && typeof value.push === 'function';

/**
 * The router: a ready instance (`config.router`) or one built from `config.routes` with the
 * history `config.history` names. On either: GA page tracking first (when `trackPages`), then
 * `config.afterEach`.
 */
function resolveRouter({ router, routes, history = 'web', base, afterEach }, { trackPages, analytics }) {
  let instance;
  if (router) {
    if (!isRouter(router)) throw new Error(`${PREFIX}({ router }) must be a vue-router instance.`);
    if (routes) throw new Error(`${PREFIX}: pass either \`router\` or \`routes\`, not both.`);
    instance = router;
  } else {
    if (!Array.isArray(routes)) throw new Error(`${PREFIX}({ routes }) is required (or pass a ready \`router\`).`);
    let routerHistory;
    if (history && typeof history === 'object') {
      routerHistory = history; // a RouterHistory the caller built
    } else if (HISTORIES[history]) {
      // `base` undefined is what createWebHistory() is called with when no base is passed
      routerHistory = HISTORIES[history](base);
    } else {
      throw new Error(`${PREFIX}: unknown history ${JSON.stringify(history)} (use 'web', 'hash', 'memory' or a RouterHistory).`);
    }
    instance = createRouter({ history: routerHistory, routes });
  }
  // A page view per navigation, `to.fullPath` — what both rdrive apps tracked by hand
  // (project-overview's router.js until 6b-2a, then its dashboard.config.js `afterEach`).
  if (trackPages) instance.afterEach((to) => analytics.trackPage(to.fullPath));
  if (afterEach) instance.afterEach(afterEach);
  return instance;
}

/**
 * The app's ROOT i18n instance. COMPOSITION mode (`legacy: false`) with a REAL fallback bundle:
 * `globalInjection` still defaults to true, so `$t(…)` and `$i18n.locale` keep working in
 * templates; `fallbackLocale` is the real 'en' bundle, so an untranslated key renders English,
 * never "" (PACKAGING_PLAN §3.6 / §5 R11).
 *
 * `messages` = the vocabulary the app passes (@rdrive/vocabulary) UNDER the app's own bundle:
 * `mergeMessages` lets the later bundle win, and no key lives in both (PLATFORM.md §6 decision 22;
 * `pnpm i18n:extract` reports any OVERRIDES). This package never imports the vocabulary itself.
 *
 * `locale` is a WATCH SOURCE (a getter): read once for the initial locale, then watched; every
 * new value is written to `i18n.global.locale.value`. It must yield a locale that has a bundle.
 * createDashboard passes the dashboard's `settings.locale` unless the config names its own source
 * — normalised at its one write site (the dashboard's `setValues`, via `normalizeLocale`), never
 * here as well. `.locale` is a WritableComputedRef in composition mode: write `.value`, never the
 * property, or the locale freezes at boot. A plain string is taken as a fixed locale (no watch).
 */
function createRootI18n({ messages, vocabulary, locale } = {}) {
  if (!messages || typeof messages !== 'object') {
    throw new Error(`${PREFIX}({ i18n: { messages } }) is required.`);
  }
  const readLocale = typeof locale === 'function' ? locale : () => locale ?? DEFAULT_LOCALE;

  const i18n = createI18n({
    legacy: false,
    locale: readLocale(),
    fallbackLocale: DEFAULT_LOCALE,
    messages: mergeMessages(vocabulary, messages),
  });

  // The same effect main.js had as `store.watch((state) => state.settings.locale, …)` (and 6b-2a
  // as a watch on the store's settings): vue's watch over that getter, default flush, no options.
  if (typeof locale === 'function') {
    watch(locale, (newLocale) => {
      i18n.global.locale.value = newLocale;
    });
  }
  return i18n;
}

/**
 * @typedef {Object} DashboardConfig
 * @property {import('vue').Component} App  the root component (project-overview: its App adapter
 *   over DashboardShell)
 * @property {import('vue-router').RouteRecordRaw[]} [routes]  required unless `router` is passed
 * @property {'web'|'hash'|'memory'|import('vue-router').RouterHistory} [history='web']
 * @property {string} [base]  the history base (default: what vue-router derives — `<base>` or '/')
 * @property {import('vue-router').Router} [router]  a ready router instead of `routes`/`history`
 * @property {import('vue-router').NavigationHookAfter} [afterEach]  registered with
 *   `router.afterEach`, after the GA page tracking
 * @property {import('vue').Plugin | ((dashboard: import('./dashboardContext.js').Dashboard) => import('vue').Plugin)} [store]
 *   a Vue plugin OBJECT, or a FUNCTION called once with the dashboard that returns one (a store
 *   whose actions need the dashboard's `conn`); installed between Vuetify and i18n
 *   (project-overview: its domain-only Vuex store). Opaque here — never imported, never read
 * @property {{ required?: string[], search?: string }} [params]  usePlatformParams' options
 * @property {{ proxyUrl: string, apiUrl?: string }} [transport]  the proxy host, and the API host
 *   `settings.apiUrl` holds until start() writes the URL's
 * @property {{ manifest?: object, onLoaded?: Function, onProgress?: Function }} [data]  the data
 *   manifest (@rdrive/data-loader) and its hooks; `onLoaded` gets `{ conn, duckdb, params,
 *   dashboard, filters }` and runs before `dataLoaded` flips
 * @property {Record<string, object | Function>} [filters]  name → a createFilterContext config
 *   (without `conn`) or a factory `(dashboard) => context`
 * @property {{ measurementId: string, getContext?: Function, trackPages?: boolean, load?: boolean }} [analytics]
 *   createAnalytics' inputs; `getContext(dashboard)` defaults to `{ parent_url: settings.apiUrl }`,
 *   `trackPages` (default true) registers a page view per navigation
 * @property {boolean} [dev=false]  install the `window.__rdriveDev` token-refresh hook (pass
 *   `import.meta.env.DEV`)
 * @property {import('@rdrive/theme').DashboardVuetifyOptions} [vuetify]  createDashboardVuetify's
 *   options: `defaultTheme`, `themeOverrides`, `icons`, `components`, `directives`
 * @property {{ messages: object, vocabulary?: object, locale?: (() => string)|string }} i18n
 *   the app's bundle, the vocabulary to put under it, and the locale watch source (default: the
 *   dashboard's `settings.locale`)
 */

/**
 * Create the dashboard's Vue app — the dashboard provided, router, Vuetify, the app's store and
 * i18n installed in that order — and return it unmounted. The dashboard is reachable with
 * useDashboard() in any component, or `app.runWithContext(useDashboard)`.
 *
 * @param {DashboardConfig} config
 * @returns {import('vue').App}
 */
export function createDashboard(config = {}) {
  const { App, store, vuetify: vuetifyOptions = {}, i18n: i18nConfig = {}, analytics } = config;
  if (!App) throw new Error(`${PREFIX}({ App }) is required.`);

  // 6b-2b: the dashboard first — its services exist before anything that uses them (the store
  // factory, the page tracking, the locale source). Creating it has no side effect.
  const dashboard = createDashboardContext(config);

  // Then, as main.js did: the router (router.js ran at import), i18n and its locale watch,
  // Vuetify; install order router → Vuetify → store → i18n.
  const router = resolveRouter(config, {
    trackPages: !!analytics?.measurementId && analytics.trackPages !== false,
    analytics: dashboard.analytics,
  });
  const i18n = createRootI18n({
    ...i18nConfig,
    locale: i18nConfig.locale !== undefined ? i18nConfig.locale : () => dashboard.settings.locale,
  });
  const vuetify = createDashboardVuetify(vuetifyOptions);
  // A function is the app's store FACTORY (a Vue plugin object is used as is).
  const storePlugin = typeof store === 'function' ? store(dashboard) : store;

  const app = createApp(App);
  app.provide(DASHBOARD_KEY, dashboard);
  app.use(router);
  app.use(vuetify);
  if (storePlugin) app.use(storePlugin);
  app.use(i18n);
  return app;
}
